import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldLabel,
    Input,
    Textarea,
} from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

type Category = RouterOutput['product']['community']['categories']['listCategories']['categories'][number]

type CategoryDialogProps = {
    open: boolean
    category?: Category | null
    onOpenChange: (open: boolean) => void
    onSubmit: (input: { title: string; description: string }) => Promise<void>
}

export const CategoryDialog = ({ open, category, onOpenChange, onSubmit }: CategoryDialogProps) => {
    const { t } = useTranslation()
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (!open) {
            return
        }

        setTitle(category?.title || '')
        setDescription(category?.description || '')
    }, [open, category])

    const handleSubmit = async () => {
        if (!title.trim() || isSubmitting) {
            return
        }

        setIsSubmitting(true)

        try {
            await onSubmit({
                title: title.trim(),
                description: description.trim(),
            })
            onOpenChange(false)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {category
                            ? t('product.community.home.categoryDialog.titleEdit')
                            : t('product.community.home.categoryDialog.titleCreate')}
                    </DialogTitle>
                    <DialogDescription>{t('product.community.home.categoryDialog.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <Field>
                        <FieldLabel required>{t('product.community.home.categoryDialog.title')}</FieldLabel>
                        <Input value={title} maxLength={70} onChange={(event) => setTitle(event.currentTarget.value)} />
                    </Field>
                    <Field>
                        <FieldLabel>{t('product.community.home.categoryDialog.categoryDescription')}</FieldLabel>
                        <Textarea
                            value={description}
                            maxLength={250}
                            rows={3}
                            onChange={(event) => setDescription(event.currentTarget.value)}
                        />
                    </Field>
                </div>
                <DialogFooter>
                    <DialogClose
                        render={
                            <Button variant="outline" disabled={isSubmitting}>
                                {t('common.cancel')}
                            </Button>
                        }
                    />
                    <Button loading={isSubmitting} disabled={!title.trim()} onClick={() => void handleSubmit()}>
                        {category ? t('common.save') : t('product.community.home.categoryDialog.create')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
